import React from "react";
import SectionHeading from "./SectionHeading";
import { LANDING } from "@/constants/testIds";

/**
 * Stewardship — Responsible Capital / ESG block, written in the
 * institutional register used by Brookfield / Apollo / Carlyle.
 */
const COMMITMENTS = [
  {
    title: "Environmental Diligence",
    body:
      "Integrated environmental review across diligence, with attention to permitting, transition risk, climate-related exposure and remediation obligations.",
  },
  {
    title: "Social & Workforce",
    body:
      "Engagement with industrial operators on workforce continuity, health & safety, training and the communities in which assets operate.",
  },
  {
    title: "Governance & Integrity",
    body:
      "Documented investment committee, independent risk oversight, robust AML/KYC and proactive conflicts management across the partnership.",
  },
];

const Stewardship = () => {
  return (
    <section
      id="stewardship"
      data-testid={LANDING.stewardship}
      className="relative py-32 lg:py-44 border-t border-[var(--kr-line-soft)] overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.08]"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1518709268805-4e9042af2176?auto=format&fit=crop&w=2400&q=70')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "grayscale(100%) contrast(1.1)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, var(--kr-bg) 0%, transparent 30%, transparent 70%, var(--kr-bg) 100%)",
        }}
      />

      <div className="relative max-w-[1480px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-16 mb-16">
          <div className="lg:col-span-6">
            <SectionHeading
              index="13"
              label="Responsible Capital · Stewardship"
              title={
                <>
                  Capital deployed with{" "}
                  <span className="italic text-[var(--kr-gold)]">
                    institutional
                  </span>{" "}
                  responsibility.
                </>
              }
            />
          </div>
          <div className="lg:col-span-6 lg:pl-10">
            <p className="text-[var(--kr-text-muted)] text-[17px] leading-[1.85] max-w-[60ch]">
              Stewardship is treated as an underwriting input rather than a
              communications exercise. Environmental, social and governance
              factors are integrated into diligence, structuring and ongoing
              engagement, with a preference for documented improvements over
              declaratory positioning.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-0 border-t border-[var(--kr-line)]">
          {COMMITMENTS.map((c, i) => (
            <div
              key={c.title}
              className={`py-12 px-2 md:px-10 ${
                i < COMMITMENTS.length - 1
                  ? "md:border-r border-[var(--kr-line)]"
                  : ""
              }`}
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="w-8 h-px bg-[var(--kr-gold)]/60" />
              </div>
              <h3 className="font-serif-display text-[1.55rem] text-[var(--kr-text)] mb-5">
                {c.title}
              </h3>
              <p className="text-[var(--kr-text-muted)] text-[15px] leading-[1.75]">
                {c.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stewardship;
